import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { ago } from '../../../app/format'
import { ApiFailure } from '../../../app/errors'
import { useProjects, useProjectsSummary } from '../../../app/queries'
import { cn } from '../../../components/cn'
import { t } from '../../../copy'
import { T } from '../../../copy/text'
import { PJ } from '../../../copy/pjshell-text'
import { hubT } from './copy'
import {
  isNeedsAttention,
  rowActivityAt,
  rowEnvStatus,
  rowHealth,
  rowReleaseCopyId,
  rowResourceKind,
  rowTaskCopyId,
  summarize,
  summarizeFromApi,
  summaryHasUnverified,
  visibleProjects,
  type HubFact,
  type HubFilter,
  type Project,
  type RowHealth,
} from './model'

const FILTERS: HubFilter[] = ['all', 'needs_attention', 'unverified']

const TABLE_COLS =
  'minmax(0,1.4fr) 104px 92px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 88px'

/**
 * 多项目中心。导航「项目」点进来就是它。
 *
 * 当前项目只由网址 `/projects/$projectId` 决定，这里没有全局切换器。
 */
export function ProjectHubPage() {
  const projects = useProjects()
  const summaryQuery = useProjectsSummary()
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<HubFilter>('all')

  const items = projects.data?.items
  if (projects.isPending && items === undefined) return <HubLoading />
  if (projects.error && items === undefined) {
    return <HubError error={projects.error} onRetry={() => void projects.refetch()} />
  }

  const listed = items ?? []
  const listOk = Boolean(projects.data) && !projects.error
  const summary = summarizeFromApi(
    summaryQuery.isPending ? 'pending' : summaryQuery.error ? 'error' : 'success',
    summaryQuery.data,
  )
  const visible = visibleProjects(listed, query, filter)
  const emptyList = listOk && listed.length === 0
  const filteredEmpty = !emptyList && visible.length === 0

  return (
    <div
      data-testid="project-hub"
      className="projects-theme bg-npc-lulu-bg-0 px-npc-grid-lg pb-10 pt-7 text-npc-lulu-t2 max-[600px]:px-npc-gap-lg"
    >
      <header className="mb-7 flex items-start justify-between gap-npc-grid max-[600px]:flex-col max-[600px]:items-stretch">
        <div className="min-w-0">
          <h1 className="m-0 font-npc-display text-npc-h1-sm font-semibold tracking-[-0.045em] text-npc-lulu-t1">
            {hubT('pjhub.title')}
          </h1>
          <p className="mt-npc-gap-xs mb-0 text-npc-body-sm font-medium leading-[1.65] text-npc-lulu-t3">
            {listOk ? hubT('pjhub.subtitle') : hubT('pjhub.subtitle.unverified')}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-npc-gap-sm">
          <ImportLink />
          <CreateLink />
        </div>
      </header>

      {projects.error ? (
        <Notice
          tone="danger"
          title={hubT('pjhub.error.title')}
          body={hubT('pjhub.error.body')}
          action={
            <button type="button" className={ghostButtonClass} onClick={() => void projects.refetch()}>
              {hubT('pjhub.action.retry')}
            </button>
          }
        />
      ) : null}

      <SummaryStrip summary={summary} />

      {listOk && summaryHasUnverified(summary) ? (
        <Notice
          tone="warn"
          title={hubT('pjhub.notice.unverified.title')}
          body={
            summary.runningTasks.status === 'value'
              ? hubT('pjhub.notice.missing_fields.body')
              : `${hubT('pjhub.notice.unverified.body')} ${hubT('pjhub.notice.unverified.why')}`
          }
        />
      ) : null}

      {!listOk ? (
        <Notice
          tone="warn"
          title={hubT('pjhub.notice.unverified.title')}
          body={`${hubT('pjhub.notice.unverified.body')} ${hubT('pjhub.notice.unverified.why')}`}
        />
      ) : null}

      {emptyList ? (
        <EmptyState />
      ) : (
        <>
          <div className="mb-npc-grid flex flex-wrap items-center gap-npc-gap-sm">
            <label className="min-w-0 flex-1 basis-[220px]">
              <span className="sr-only">{hubT('pjhub.search.placeholder')}</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={hubT('pjhub.search.placeholder')}
                className="pj-input h-9 w-full border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-3.5 text-npc-body-sm font-medium text-npc-lulu-t1 outline-none placeholder:text-npc-lulu-t4"
              />
            </label>
            <div role="tablist" aria-label={hubT('pjhub.filter.all')} className="flex flex-wrap gap-npc-gap-xs">
              {FILTERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={filter === key}
                  className={cn(
                    'h-[30px] rounded-full border px-3 text-npc-caption font-medium',
                    filter === key
                      ? 'pj-filter-active'
                      : 'border-npc-lulu-line-2 bg-transparent text-npc-lulu-t2',
                  )}
                  onClick={() => setFilter(key)}
                >
                  {hubT(
                    key === 'all'
                      ? 'pjhub.filter.all'
                      : key === 'needs_attention'
                        ? 'pjhub.filter.needs_attention'
                        : 'pjhub.filter.unverified',
                  )}
                </button>
              ))}
            </div>
          </div>

          {filteredEmpty ? (
            <p className="m-0 text-npc-body-sm text-npc-lulu-t3">{hubT('pjhub.filter.empty')}</p>
          ) : (
            <>
              <p className="pj-caption mb-npc-gap-sm mt-0 text-npc-meta">{hubT('pjhub.sort.hint')}</p>
              <ProjectTable items={visible} />
              <ProjectCards items={visible} />
            </>
          )}

          <div className="pj-card mt-npc-card px-[15px] py-[13px]">
            <div className="text-npc-body-sm font-semibold text-npc-lulu-t1">
              {hubT('pjhub.notice.not_switcher.title')}
            </div>
            <p className="mb-0 mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-lulu-t2">
              {hubT('pjhub.notice.not_switcher.body')}
            </p>
          </div>
        </>
      )}
    </div>
  )
}

function HubLoading() {
  return (
    <div
      data-testid="project-hub-loading"
      aria-busy="true"
      className="projects-theme bg-npc-lulu-bg-0 px-npc-grid-lg pb-npc-card-lg pt-npc-card max-[600px]:px-npc-gap-lg"
    >
      <p className="mt-0 mb-npc-gap-xs text-npc-body-sm text-npc-lulu-t2">{hubT('pjhub.loading.title')}</p>
      <p className="mt-0 mb-npc-card text-npc-body-sm text-npc-lulu-t3">{hubT('pjhub.loading.hint')}</p>
      <div className="flex flex-col gap-npc-grid">
        <div className="h-[30px] w-[220px] animate-npc-pulse rounded-npc-chip bg-npc-lulu-bg-3" />
        <div className="grid grid-cols-4 gap-npc-grid max-[600px]:grid-cols-2">
          {[0, 1, 2, 3].map((key) => (
            <div key={key} className="h-[72px] animate-npc-pulse rounded-npc-chip bg-npc-lulu-bg-2" />
          ))}
        </div>
        <div className="h-[180px] animate-npc-pulse rounded-npc-chip bg-npc-lulu-bg-2" />
      </div>
    </div>
  )
}

function HubError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const detail = error instanceof ApiFailure ? error.message : ''
  return (
    <div
      data-testid="project-hub-error"
      className="projects-theme bg-npc-lulu-bg-0 px-npc-grid-lg pb-npc-card-lg pt-npc-card max-[600px]:px-npc-gap-lg"
    >
      <h1 className="m-0 font-npc-display text-npc-h1-sm font-semibold text-npc-lulu-t1">
        {hubT('pjhub.title')}
      </h1>
      <div className="mt-npc-card max-w-[560px] rounded-npc-chip border border-npc-lulu-bad/40 bg-npc-lulu-bg-1 px-[15px] py-[13px]">
        <div className="text-npc-body-sm font-semibold text-npc-lulu-bad">{hubT('pjhub.error.title')}</div>
        <p className="mb-npc-grid mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-lulu-t2">
          {hubT('pjhub.error.body')}
          {detail ? ` ${detail}` : ''}
        </p>
        <SummaryStrip summary={summarize('error', undefined)} />
        <button type="button" className={primaryButtonClass} onClick={onRetry}>
          {hubT('pjhub.action.retry')}
        </button>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div
      data-testid="project-hub-empty"
      className="max-w-[560px] rounded-npc-chip border border-dashed border-npc-lulu-line-3 bg-npc-lulu-bg-1 px-[15px] py-[13px]"
    >
      <div className="text-npc-body-sm font-semibold text-npc-lulu-t1">{hubT('pjhub.empty.title')}</div>
      <p className="mb-npc-grid mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-lulu-t2">
        {hubT('pjhub.empty.body')}
      </p>
      <div className="flex flex-wrap gap-npc-gap-sm">
        <ImportLink />
        <CreateLink />
      </div>
    </div>
  )
}

function SummaryStrip({ summary }: { summary: ReturnType<typeof summarize> }) {
  const cells: Array<{ id: string; label: string; fact: HubFact; tone?: 'ok' | 'warn'; attn?: boolean }> = [
    { id: 'projects', label: hubT('pjhub.stat.projects'), fact: summary.projectCount },
    { id: 'healthy', label: hubT('pjhub.stat.healthy'), fact: summary.healthyCount, tone: 'ok' },
    { id: 'needs', label: hubT('pjhub.stat.needs_attention'), fact: summary.needsAttentionCount, tone: 'warn', attn: true },
    { id: 'tasks', label: hubT('pjhub.stat.running_tasks'), fact: summary.runningTasks },
    { id: 'releases', label: hubT('pjhub.stat.running_releases'), fact: summary.runningReleases },
  ]
  return (
    <div
      data-testid="project-hub-summary"
      className="mb-7 grid grid-cols-2 gap-2.5 min-[601px]:grid-cols-[1fr_1fr_1.18fr_1fr_1fr]"
    >
      {cells.map((cell) => (
        <div
          key={cell.id}
          data-testid={`project-hub-stat-${cell.id}`}
          className={cn('pj-card px-4 py-3.5', cell.attn && 'pj-metric-attn')}
        >
          <div
            className={cn(
              'pj-metric-n pj-tabular font-npc-mono text-npc-stat-sm font-semibold leading-none tracking-[-0.03em]',
              cell.tone === 'ok'
                ? 'text-npc-lulu-ok'
                : cell.tone === 'warn'
                  ? 'text-npc-lulu-warn'
                  : 'text-npc-lulu-t1',
            )}
          >
            <FactValue fact={cell.fact} />
          </div>
          <div className="pj-caption mt-npc-gap-xs text-npc-caption">{cell.label}</div>
        </div>
      ))}
    </div>
  )
}

function FactValue({ fact }: { fact: HubFact }) {
  if (fact.status === 'unverified') {
    return <span className="text-npc-section font-semibold text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  return <span>{fact.value}</span>
}

function ProjectTable({ items }: { items: Project[] }) {
  const cols = [
    { id: 'name', label: hubT('pjhub.col.name') },
    { id: 'health', label: hubT('pjhub.col.health') },
    { id: 'env', label: hubT('pjhub.col.env') },
    { id: 'task', label: hubT('pjhub.col.recent_task') },
    { id: 'release', label: hubT('pjhub.col.recent_release') },
    { id: 'resource', label: hubT('pjhub.col.resource') },
    { id: 'activity', label: hubT('pjhub.col.activity') },
  ]
  return (
    <div
      data-testid="project-hub-table"
      className="pj-shell hidden overflow-hidden min-[1024px]:block"
    >
      <div
        className="grid bg-npc-lulu-bg-2 text-npc-meta-sm text-npc-lulu-t3"
        style={{ gridTemplateColumns: TABLE_COLS }}
      >
        {cols.map((col) => (
          <div key={col.id} className="px-3 py-2">
            {col.label}
          </div>
        ))}
      </div>
      {items.map((project) => (
        <ProjectTableRow key={project.id} project={project} />
      ))}
    </div>
  )
}

function ProjectTableRow({ project }: { project: Project }) {
  const health = rowHealth(project)
  const warn = isNeedsAttention(project)
  const danger = project.health_tone === 'danger'
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      data-testid={`project-hub-row-${project.id}`}
      className={cn(
        'grid border-t border-npc-lulu-line-1 text-npc-body-sm text-npc-lulu-t1',
        warn && 'pj-attn-row',
        danger && 'bg-npc-lulu-bad/10',
      )}
      style={{ gridTemplateColumns: TABLE_COLS }}
    >
      <div className="flex min-w-0 items-center gap-npc-gap-xs px-3 py-[10px]">
        <HealthDot health={health} />
        <span className="line-clamp-2 font-semibold">{project.name}</span>
        {project.is_builtin ? <BuiltinMark /> : null}
      </div>
      <div className="flex items-center px-3 py-[10px]">
        <HealthCell health={health} />
      </div>
      <EnvCell project={project} />
      <TaskCell project={project} />
      <ReleaseCell project={project} />
      <ResourceCell project={project} />
      <ActivityCell project={project} />
    </Link>
  )
}

function ProjectCards({ items }: { items: Project[] }) {
  return (
    <div data-testid="project-hub-cards" className="grid gap-npc-grid-lg min-[1024px]:hidden">
      {items.map((project) => (
        <ProjectCardRow key={project.id} project={project} />
      ))}
    </div>
  )
}

function ProjectCardRow({ project }: { project: Project }) {
  const health = rowHealth(project)
  const warn = isNeedsAttention(project)
  const danger = project.health_tone === 'danger'
  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: project.id }}
      data-testid={`project-hub-card-${project.id}`}
      className={cn(
        'pj-card min-h-10 p-[13px] text-npc-lulu-t1',
        warn && 'pj-attn-row',
        danger && 'border-npc-lulu-bad/40',
      )}
    >
      <div className="flex items-start justify-between gap-npc-gap-sm">
        <div className="min-w-0 text-npc-section font-semibold leading-[1.4]">
          <span className="line-clamp-2">{project.name}</span>
          {project.is_builtin ? (
            <span className="mt-npc-gap-xs inline-block">
              <BuiltinMark />
            </span>
          ) : null}
        </div>
        <HealthCell health={health} />
      </div>
      <div className="mt-npc-gap-sm text-npc-caption text-npc-lulu-t3">
        {hubT('pjhub.col.env')}
        <span className="ml-npc-gap-xs">
          <EnvValue project={project} />
        </span>
      </div>
      <div className="mt-npc-gap-xs text-npc-meta text-npc-lulu-t4">
        {hubT('pjhub.col.recent_task')} · <TaskValue project={project} />
        {' · '}
        {hubT('pjhub.col.recent_release')} · <ReleaseValue project={project} />
        {' · '}
        {hubT('pjhub.col.resource')} · <ResourceValue project={project} />
        {' · '}
        {hubT('pjhub.col.activity')} · <ActivityValue project={project} />
      </div>
    </Link>
  )
}

function HealthDot({ health }: { health: RowHealth }) {
  const tone =
    health.kind === 'unverified'
      ? 'bg-npc-lulu-warn'
      : health.tone === 'ok'
        ? 'bg-npc-lulu-ok'
        : health.tone === 'warn'
          ? 'bg-npc-lulu-warn'
          : health.tone === 'danger'
            ? 'bg-npc-lulu-bad'
            : 'bg-npc-lulu-t4'
  return <span aria-hidden="true" className={cn('size-[7px] shrink-0 rounded-npc-full', tone)} />
}

function HealthCell({ health }: { health: RowHealth }) {
  if (health.kind === 'unverified') {
    return <span className="text-npc-caption text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  const color =
    health.tone === 'ok'
      ? 'text-npc-lulu-ok'
      : health.tone === 'warn'
        ? 'text-npc-lulu-warn'
        : health.tone === 'danger'
          ? 'text-npc-lulu-bad'
          : 'text-npc-lulu-t3'
  return <span className={cn('text-npc-caption', color)}>{health.text}</span>
}

function EnvCell({ project }: { project: Project }) {
  return (
    <div className="flex items-center px-3 py-[10px] text-npc-caption">
      <EnvValue project={project} />
    </div>
  )
}

function EnvValue({ project }: { project: Project }) {
  return (
    <span>
      <EnvWord raw={project.production_env_status} />
      {' / '}
      <EnvWord raw={project.test_env_status} />
    </span>
  )
}

function EnvWord({ raw }: { raw: string | null | undefined }) {
  const kind = rowEnvStatus(raw)
  if (kind === 'unverified') {
    return <span className="text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  if (kind === 'no_bundle') {
    return <PJ id="pjshell.env.no_bundle" />
  }
  const id =
    kind === 'ready'
      ? 'pjshell.env.status.ready'
      : kind === 'failed'
        ? 'pjshell.env.status.failed'
        : 'pjshell.env.status.rolled_back'
  return <PJ id={id} />
}

function TaskCell({ project }: { project: Project }) {
  return (
    <div className="flex items-center px-3 py-[10px] text-npc-caption">
      <TaskValue project={project} />
    </div>
  )
}

function TaskValue({ project }: { project: Project }) {
  const copyId = rowTaskCopyId(project.latest_task_state)
  if (!copyId) {
    return <span className="text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  return <T id={copyId} />
}

function ReleaseCell({ project }: { project: Project }) {
  return (
    <div className="flex items-center px-3 py-[10px] text-npc-caption">
      <ReleaseValue project={project} />
    </div>
  )
}

function ReleaseValue({ project }: { project: Project }) {
  const copyId = rowReleaseCopyId(project.latest_release_status)
  if (!copyId) {
    return <span className="text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  return <PJ id={copyId} />
}

function ResourceCell({ project }: { project: Project }) {
  return (
    <div className="flex items-center px-3 py-[10px] text-npc-caption">
      <ResourceValue project={project} />
    </div>
  )
}

function ResourceValue({ project }: { project: Project }) {
  const kind = rowResourceKind(project.resource_anomaly)
  if (kind === 'unverified') {
    return <span className="text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  return <span>{kind === 'has' ? hubT('pjhub.resource.has') : hubT('pjhub.resource.none')}</span>
}

function ActivityCell({ project }: { project: Project }) {
  return (
    <div className="flex items-center px-3 py-[10px] text-npc-caption">
      <ActivityValue project={project} />
    </div>
  )
}

function ActivityValue({ project }: { project: Project }) {
  const at = rowActivityAt(project.last_activity_at)
  if (!at) {
    return <span className="text-npc-lulu-warn">{hubT('pjhub.value.unverified')}</span>
  }
  return <span className="pj-tabular font-npc-mono text-npc-lulu-t3">{ago(at)}</span>
}

function BuiltinMark() {
  return (
    <span className="rounded-npc-tag-sm bg-npc-lulu-bg-3 px-1.5 py-0.5 text-npc-meta text-npc-lulu-t3">
      {hubT('pjhub.builtin')}
    </span>
  )
}

function Notice({
  tone,
  title,
  body,
  action,
}: {
  tone: 'warn' | 'danger'
  title: string
  body: string
  action?: ReactNode
}) {
  return (
    <div
      className={cn(
        'mb-npc-grid rounded-npc-chip border px-[15px] py-[13px]',
        tone === 'warn' ? 'border-npc-lulu-warn/35 bg-npc-lulu-bg-1' : 'border-npc-lulu-bad/40 bg-npc-lulu-bg-1',
      )}
    >
      <div
        className={cn(
          'text-npc-body-sm font-semibold',
          tone === 'warn' ? 'text-npc-lulu-warn' : 'text-npc-lulu-bad',
        )}
      >
        {title}
      </div>
      <p className="mb-0 mt-npc-gap-xs text-npc-caption leading-[1.65] text-npc-lulu-t2">{body}</p>
      {action ? <div className="mt-npc-gap-sm">{action}</div> : null}
    </div>
  )
}

const primaryButtonClass =
  'pj-btn-primary inline-flex h-[34px] items-center justify-center rounded-full px-3.5 text-npc-caption font-semibold'

const secondaryButtonClass =
  'pj-btn-ghost inline-flex h-[34px] items-center justify-center rounded-full px-3.5 text-npc-caption font-medium'

const ghostButtonClass =
  'pj-btn-ghost inline-flex h-[34px] items-center justify-center rounded-full px-3.5 text-npc-caption font-medium'

function ImportLink() {
  return (
    <Link to="/projects/import" className={secondaryButtonClass}>
      {t('project_import.action.start')}
    </Link>
  )
}

function CreateLink() {
  return (
    <Link to="/projects/new" data-testid="projects-new" className={primaryButtonClass}>
      {hubT('pjhub.action.create')}
      <span className="pj-btn-aff" aria-hidden="true">
        <Plus size={12} strokeWidth={1.6} />
      </span>
    </Link>
  )
}
